import { isStripeCheckout } from '../../../lib/checkout-mode.js';
import { useEffect, useReducer, useRef, useState } from 'react';
import { attachEscapeDismiss } from '../../../lib/escape-dismiss.js';
import { 
  LayoutGrid,
  ShoppingCart, 
  Menu, 
  Phone, 
  Mail, 
  Search, 
  ChevronDown, 
  Truck, 
  Send,
  Layers,
  Bot,
  LayoutDashboard
} from 'lucide-react';
import { Button } from '../../../components/ui/Button.jsx';
import { useTenant } from '../../../context/TenantContext.jsx';
import { showInternalTools } from '../../../lib/internal-tools.js';
import { resolveMenuRoots } from '../../../lib/db-category-tree.js';
import { TenantLogo } from '../../../components/tenant/TenantLogo.jsx';
import { getTopbarTexts } from '../../../lib/storefront-texts.js';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../../../components/ui/Sheet.jsx';
import { useAslCategoryTree } from '../../../lib/use-asl-category-tree.js';
import { isPlainLeftClick } from '../../../lib/click.js';
import { MobileAslNode } from './MobileAslNode.jsx';
import { buildRouteUrl } from '../../../lib/router.js';
import { cn } from '../../../lib/utils.js';
import { SearchOverlay } from '../../../components/search/SearchOverlay.jsx';

/**
 * a-050: Kurzes Nav-Label für die Rubrik (Desktop-Trigger). Der Datenstand (D1 categories.name)
 * bleibt unverändert; nur die Anzeige im Trigger wird gekürzt. Unterpunkte bleiben im Menü.
 */
const NAV_LABEL_BY_SLUG = Object.freeze({
  'stromversorgung-infrastruktur': 'Stromversorgung',
});
export function navRootLabel(cat) {
  return NAV_LABEL_BY_SLUG[cat?.slug] || cat?.name || '';
}

/** Klicks innerhalb von Trigger oder Panel gelten als „innen“ und schließen das Menü nicht (B1). */
export function isInsideMegaMenu(target) {
  return Boolean(target && typeof target.closest === 'function' && target.closest('[data-asl-mega-root]'));
}

const INITIAL_ENTERPRISE_MEGA_MENU_STATE = Object.freeze({ open: false, hovered: false });

export function isEnterpriseMegaMenuVisible(state) {
  return Boolean(state && (state.open || state.hovered));
}

export function enterpriseMegaMenuReducer(state, action) {
  switch (action.type) {
    case 'pointer-enter': {
      const next = { ...state, hovered: true };
      if (action.key !== undefined) next.activeKey = action.key;
      return next;
    }
    case 'pointer-leave':
      return { ...state, hovered: false };
    case 'toggle': {
      // Klick schließt nur ein per Klick geöffnetes Menü. Ein nur per Hover sichtbares Menü wird fixiert (a-044).
      const isPinned = Boolean(state.open);
      const isSameKey = action.key === undefined || state.activeKey === action.key;
      if (isPinned && isSameKey) {
        const next = { ...state, open: false, hovered: false };
        delete next.activeKey;
        return next;
      }
      const next = { ...state, open: true, hovered: false };
      if (action.key !== undefined) next.activeKey = action.key;
      return next;
    }
    case 'set-active': {
      const next = { ...state };
      if (action.key !== undefined) next.activeKey = action.key;
      return next;
    }
    case 'escape':
    case 'close': {
      const next = { ...state, open: false, hovered: false };
      delete next.activeKey;
      return next;
    }
    default:
      return state;
  }
}

export function EnterpriseHeader({
  tenant,
  cartCount = 0,
  onOpenCart,
  onNavigate,
  onSearch,
  currentPath = '/',
  onOpenCopilot,
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const leaveTimerRef = useRef(null);

  const [megaMenuState, dispatchMegaMenu] = useReducer(
    enterpriseMegaMenuReducer,
    INITIAL_ENTERPRISE_MEGA_MENU_STATE,
  );
  const megaMenuVisible = isEnterpriseMegaMenuVisible(megaMenuState);

  const cancelLeaveTimer = () => {
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
  };

  const scheduleLeaveTimer = () => {
    cancelLeaveTimer();
    leaveTimerRef.current = setTimeout(() => {
      dispatchMegaMenu({ type: 'pointer-leave' });
    }, 220);
  };

  useEffect(() => {
    return () => cancelLeaveTimer();
  }, []);

  // Escape schließt das Menü auch bei reinem Maus-Hover (Fokus liegt dann nicht im Menü), WCAG 1.4.13.
  useEffect(() => {
    if (!megaMenuVisible || typeof document === 'undefined') return undefined;
    return attachEscapeDismiss(document, () => dispatchMegaMenu({ type: 'escape' }));
  }, [megaMenuVisible]);

  // Außenklick schließt das Mega-Menü (aria-expanded wieder false).
  useEffect(() => {
    if (!megaMenuVisible || typeof document === 'undefined') return undefined;
    const handlePointerDown = (event) => {
      if (isInsideMegaMenu(event.target)) return;
      cancelLeaveTimer();
      dispatchMegaMenu({ type: 'close' });
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [megaMenuVisible]);

  // Demo-Tenants: interne Einstiege (Hub, ALWIS) ausblenden (qa-only F-01)
  let ctxTenant = null;
  try {
    const ctx = useTenant();
    ctxTenant = ctx?.tenant || null;
  } catch {
    // außerhalb eines TenantProvider (Tests, isoliertes Rendering)
  }
  const showInternal = showInternalTools((tenant || ctxTenant)?.settings);
  const topbarTexts = getTopbarTexts(tenant || ctxTenant);
  const { tree: aslCategoryTree = [], categories: dbCategories = [], status: aslCategoryTreeStatus } = useAslCategoryTree(tenant?.id ?? ctxTenant?.id);
  const aslMenuRoots = resolveMenuRoots({ aslTree: aslCategoryTree, aslStatus: aslCategoryTreeStatus, dbCategories });

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const query = searchQuery.trim();
    if (onSearch) onSearch(query);
    if (onNavigate && query) {
      onNavigate(`/catalog?search=${encodeURIComponent(query)}`);
    }
  };

  const handleNav = (path) => {
    cancelLeaveTimer();
    dispatchMegaMenu({ type: 'close' });
    if (onNavigate) onNavigate(path);
  };

  const categoryUrl = (category) => category.url || buildRouteUrl({ name: 'catalog', aslCategory: category.key });
  const handleAslCategoryClick = (event, category) => {
    if (!onNavigate) return;
    // Strg/Cmd/Umschalt/Alt, Mittelklick: Browser öffnet neuen Tab bzw. Fenster, die App navigiert nicht.
    if (!isPlainLeftClick(event)) return;
    event.preventDefault();
    handleNav(categoryUrl(category));
  };
  const displayCartCount = cartCount > 99 ? '99+' : String(cartCount);

  return (
    <header
      data-header-variant="enterprise"
      className="sticky top-0 z-40 w-full border-b border-[var(--border)] bg-white shadow-xs"
      onMouseEnter={() => {
        if (megaMenuState.hovered) cancelLeaveTimer();
      }}
      onMouseLeave={() => {
        if (megaMenuState.hovered) scheduleLeaveTimer();
      }}
    >
      {/* Tier 1: Utility & B2B Status Bar */}
      <div className="border-b border-[var(--border)] bg-[var(--surface-container-low,#f8fafc)] text-xs text-[var(--secondary,#475569)] overflow-hidden">
        <div className="mx-auto flex h-9 w-full max-w-[1440px] items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 min-w-0">
            <div className="hidden md:flex items-center gap-1.5 text-[var(--secondary)] shrink-0">
              <Truck className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
              <span>{topbarTexts.topbar_shipping}</span>
            </div>
          </div>

          <div className="hidden sm:flex items-center gap-4 shrink-0">
            {tenant?.contactEmail && (
              <a
                href={`mailto:${tenant.contactEmail}`}
                className="hidden sm:flex items-center gap-1 hover:text-[var(--primary)] transition-colors"
              >
                <Mail className="h-3.5 w-3.5 shrink-0" />
                <span>{tenant.contactEmail}</span>
              </a>
            )}
            {tenant?.phone && (
              <a
                href={`tel:${tenant.phone.replace(/[^0-9+]/g, '')}`}
                className="flex items-center gap-1 font-bold text-[var(--foreground)] hover:text-[var(--primary)] transition-colors"
              >
                <Phone className="h-3.5 w-3.5 text-[var(--primary)] shrink-0" />
                <span>{`Hotline: ${tenant.phone}`}</span>
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Tier 2: Main Enterprise Navigation Bar */}
      <div className="mx-auto flex h-16 w-full max-w-[1920px] min-w-0 items-center justify-between gap-3 xl:gap-4 px-4 sm:px-6 lg:px-8">
        
        {/* Brand Logo & Rubrik Mega Menu Triggers */}
        <div className="flex items-center min-w-0 gap-3 xl:gap-4 flex-1">
          <button
            onClick={() => handleNav('/')}
            className="flex items-center text-left transition-opacity hover:opacity-90 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)] rounded shrink-0"
            aria-label="Startseite aufrufen"
          >
            <TenantLogo tenant={tenant} showSlogan={false} />
          </button>

          {/* 5 ASL Rubriken als eigene Nav-Buttons (Desktop) */}
          <nav aria-label="Hauptnavigation" className="hidden lg:flex items-center flex-1 justify-center min-w-0 gap-2 overflow-hidden">
            {aslMenuRoots.map((cat) => {
              const isThisTriggerActive = megaMenuVisible && (megaMenuState.activeKey === cat.key || (!megaMenuState.activeKey && cat.key === aslMenuRoots[0]?.key));
              return (
                <div
                  key={cat.key}
                  data-asl-mega-root=""
                  className="relative flex items-center shrink-0 min-w-0"
                  onMouseEnter={() => {
                    cancelLeaveTimer();
                    dispatchMegaMenu({ type: 'pointer-enter', key: cat.key });
                  }}
                  onMouseLeave={() => {
                    scheduleLeaveTimer();
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') {
                      event.preventDefault();
                      cancelLeaveTimer();
                      dispatchMegaMenu({ type: 'escape' });
                      event.currentTarget.querySelector('button')?.focus();
                    }
                  }}
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      cancelLeaveTimer();
                      dispatchMegaMenu({ type: 'toggle', key: cat.key });
                    }}
                    className={cn(
                      "h-10 min-w-0 px-2 font-bold text-xs gap-1 transition-colors cursor-pointer",
                      isThisTriggerActive
                        ? "bg-[var(--surface-container-high,#f1f5f9)] text-[var(--primary)]"
                        : "text-[var(--foreground)] hover:bg-[var(--surface-container-low,#f8fafc)] hover:text-[var(--primary)]"
                    )}
                    aria-expanded={isThisTriggerActive}
                    aria-controls={`enterprise-mega-menu-panel-${cat.key}`}
                    aria-label={`${navRootLabel(cat)} Menü öffnen`}
                    data-asl-category-key={cat.key}
                    data-asl-surface="desktop-rubrik-trigger"
                    data-testid={`mega-menu-trigger-${cat.key}`}
                  >
                    <span className="truncate">{navRootLabel(cat)}</span>
                    <ChevronDown className={cn('h-3.5 w-3.5 opacity-70 transition-transform shrink-0', isThisTriggerActive && 'rotate-180')} />
                  </Button>
                  {/* Brücke Trigger→Panel: fängt die diagonale Übergangszone ab und cancelt den Close-Timer */}
                  <div
                    aria-hidden="true"
                    data-testid="mega-menu-pointer-bridge"
                    className="absolute left-0 right-0 top-full h-3 z-50"
                    onMouseEnter={() => {
                      cancelLeaveTimer();
                      dispatchMegaMenu({ type: 'pointer-enter', key: cat.key });
                    }}
                  />
                </div>
              );
            })}
          </nav>
        </div>

        {/* Action CTAs: Betriebsleiter Hub + Copilot + Suche + Projektanfrage + Cart + Mobile Burger */}
        <div className="flex items-center gap-2 sm:gap-2.5 shrink-0">
          
          {/* Betriebsleiter Hub Link (nur ASL-intern, nicht für Demo-Tenants) */}
          {showInternal && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="executive-hub-header-trigger"
            onClick={() => handleNav('/hub')}
            className="h-10 gap-1.5 px-2.5 font-bold text-[var(--foreground)] hover:bg-[var(--surface-container-low)] cursor-pointer"
            aria-label="Betriebsleiter Hub öffnen"
          >
            <LayoutDashboard className="h-4 w-4 text-[var(--primary)]" />
            <span className="hidden xl:inline">Betriebsleiter Hub</span>
          </Button>
          )}

          {/* ALWIS Copilot Trigger (nur ASL-intern) */}
          {showInternal && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="copilot-header-trigger"
            onClick={onOpenCopilot}
            className="h-10 gap-1.5 px-2.5 font-bold text-[var(--foreground)] hover:bg-[var(--surface-container-low)] cursor-pointer"
            aria-label="ALWIS Fachassistent öffnen"
          >
            <Bot className="h-4 w-4 text-[var(--primary)]" />
            <span className="hidden lg:inline">ALWIS Assistent</span>
          </Button>
          )}

          {/* Search Icon Button (Desktop) */}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setIsSearchOpen(true)}
            className="hidden lg:flex h-10 w-10 text-[var(--foreground)] hover:bg-[var(--surface-container-low)] cursor-pointer"
            aria-label="Suche öffnen"
          >
            <Search className="h-4 w-4 text-[var(--primary)]" />
          </Button>

          {/* Projektanfrage Button: nur im Anfrage-Modus (im Kauf-Modus entfällt er, a-050) */}
          {!isStripeCheckout(tenant || ctxTenant) && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleNav('/kontakt')}
            data-testid="header-project-inquiry"
            className="hidden sm:flex h-10 gap-1.5 px-3 font-bold border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--primary)]/10 cursor-pointer"
          >
            <Send className="h-3.5 w-3.5" />
            <span>Projektanfrage</span>
          </Button>
          )}

          {/* Cart Button */}
          <Button
            variant="default"
            size="sm"
            onClick={() => {
              if (onOpenCart) onOpenCart();
              else handleNav('/cart');
            }}
            className="h-10 gap-2 px-3 font-bold shadow-sm cursor-pointer"
            aria-label={`${isStripeCheckout(tenant || ctxTenant) ? 'Warenkorb' : 'Anfragekorb'} öffnen mit ${cartCount} Positionen`}
          >
            <ShoppingCart className="h-4 w-4" />
            <span className="hidden md:inline">{isStripeCheckout(tenant || ctxTenant) ? 'Warenkorb' : 'Anfragekorb'}</span>
            <span
              data-testid="cart-count-badge"
              className={cn(
                'flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-bold leading-none transition-colors',
                cartCount > 0
                  ? 'bg-white text-[var(--primary,#e3000b)] font-extrabold shadow-2xs'
                  : 'bg-white/30 text-white'
              )}
            >
              {displayCartCount}
            </span>
          </Button>

          {/* Mobile Menu */}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="min-h-[44px] min-w-[44px] lg:hidden" aria-label="Menü öffnen">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[320px] sm:w-[380px] overflow-y-auto">
              <SheetHeader className="mb-4">
                <SheetTitle className="text-left font-display">
                  <TenantLogo tenant={tenant} showSlogan={false} />
                </SheetTitle>
              </SheetHeader>

              <div className="space-y-4">
                <form onSubmit={handleSearchSubmit} className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-2.5 text-muted-foreground" />
                  <input
                    type="search"
                    placeholder="Katalog nach SKU suchen..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-9 w-full rounded-[var(--radius)] border border-[var(--border)] pl-9 pr-3 text-xs"
                  />
                </form>

                <div className="space-y-1">
                  <SheetClose asChild>
                    <button
                      onClick={() => handleNav('/catalog')}
                      className="flex w-full items-center gap-3 rounded-[var(--radius)] px-3 py-2 text-left text-xs font-bold hover:bg-[var(--surface-container-low)] cursor-pointer"
                    >
                      <LayoutGrid className="h-4 w-4 text-[var(--primary)]" />
                      Alle Produkte
                    </button>
                  </SheetClose>

                  {showInternal && (
                  <SheetClose asChild>
                    <button
                      onClick={() => handleNav('/hub')}
                      data-testid="executive-hub-mobile-trigger"
                      className="flex w-full items-center gap-3 rounded-[var(--radius)] px-3 py-2 text-left text-xs font-bold text-[var(--primary)] hover:bg-[var(--surface-container-low)] cursor-pointer"
                    >
                      <LayoutDashboard className="h-4 w-4 text-[var(--primary)]" />
                      Betriebsleiter Hub
                    </button>
                  </SheetClose>
                  )}

                  <div className="my-2 border-t border-[var(--border)]" />

                  {aslMenuRoots.map((category) => (
                    <MobileAslNode
                      key={category.key}
                      category={category}
                      categoryUrl={categoryUrl}
                      onCategoryClick={handleAslCategoryClick}
                      wrapLink={(anchor) => <SheetClose asChild>{anchor}</SheetClose>}
                    />
                  ))}
                </div>

                {tenant?.phone && (
                  <div className="border-t border-[var(--border)] pt-4">
                    <p className="text-xs font-semibold text-muted-foreground mb-1">Technische Fachberatung</p>
                    <a
                      href={`tel:${tenant.phone.replace(/[^0-9+]/g, '')}`}
                      className="flex items-center gap-2 text-sm font-bold text-[var(--primary)]"
                    >
                      <Phone className="h-4 w-4" />
                      {tenant.phone}
                    </a>
                  </div>
                )}
              </div>
            </SheetContent>
          </Sheet>

        </div>
      </div>

      {/* Mega Menu Popup Panels (Full Viewport Width per Rubrik) */}
      {aslMenuRoots.map((cat) => {
        const isThisPanelVisible = megaMenuVisible && (megaMenuState.activeKey === cat.key || (!megaMenuState.activeKey && cat.key === aslMenuRoots[0]?.key));
        const groups = Array.isArray(cat.children) ? cat.children : [];
        return (
          <div
            key={cat.key}
            id={`enterprise-mega-menu-panel-${cat.key}`}
            data-testid="enterprise-mega-menu-panel"
            data-asl-mega-root=""
            data-asl-category-key={cat.key}
            aria-label={`${cat.name} Produktkategorien`}
            onMouseEnter={() => {
              cancelLeaveTimer();
              dispatchMegaMenu({ type: 'pointer-enter', key: cat.key });
            }}
            onMouseLeave={() => {
              scheduleLeaveTimer();
            }}
            className={cn(
              'hidden lg:block invisible absolute left-0 right-0 top-full z-50 w-full border-b border-[var(--border)] bg-white shadow-2xl transition-all duration-200 ease-out origin-top',
              isThisPanelVisible ? 'visible opacity-100 translate-y-0' : '-translate-y-1 opacity-0 pointer-events-none'
            )}
          >
            <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8 py-6">
              <div className="flex items-center justify-between border-b border-[var(--border)] pb-3 mb-5">
                <div className="flex items-center gap-2">
                  <Layers className="h-4 w-4 text-[var(--primary)]" />
                  <h4 className="font-display text-sm font-bold uppercase tracking-wider text-[var(--foreground)]">
                    {cat.name}
                  </h4>
                </div>
                <div className="flex items-center gap-4">
                  <a
                    href={categoryUrl(cat)}
                    onClick={(event) => handleAslCategoryClick(event, cat)}
                    data-asl-surface="desktop-mega-menu"
                    data-asl-category-key={cat.key}
                    data-asl-level={cat.level}
                    className="text-xs font-bold text-[var(--primary)] hover:underline cursor-pointer"
                  >
                    Alle {cat.name}-Produkte ansehen &rarr;
                  </a>
                  <button
                    onClick={() => handleNav('/catalog')}
                    className="text-xs font-semibold text-[var(--secondary)] hover:text-[var(--primary)] hover:underline cursor-pointer"
                  >
                    Gesamten Katalog ansehen &rarr;
                  </button>
                </div>
              </div>

              {groups.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
                  {groups.map((group) => {
                    const subChildren = Array.isArray(group.children) ? group.children : [];
                    return (
                      <div key={group.key} className="space-y-2 min-w-0">
                        <a
                          href={categoryUrl(group)}
                          onClick={(event) => handleAslCategoryClick(event, group)}
                          data-asl-surface="desktop-mega-menu"
                          data-asl-category-key={group.key}
                          data-asl-level={group.level}
                          className="group/cat flex w-full items-center justify-between cursor-pointer text-left font-display text-xs font-bold text-[var(--foreground)] hover:text-[var(--primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]"
                        >
                          <span className="truncate">{group.name}</span>
                          <ChevronDown className="h-3 w-3 -rotate-90 text-[var(--primary)] opacity-0 transition-opacity group-hover/cat:opacity-100 shrink-0" />
                        </a>
                        {subChildren.length > 0 && (
                          <ul className="space-y-1 border-l border-[var(--border)] pl-2">
                            {subChildren.map((sub) => (
                              <li key={sub.key}>
                                <a
                                  href={categoryUrl(sub)}
                                  onClick={(event) => handleAslCategoryClick(event, sub)}
                                  data-asl-surface="desktop-mega-menu"
                                  data-asl-category-key={sub.key}
                                  data-asl-level={sub.level}
                                  className="block w-full truncate cursor-pointer text-left text-xs text-[var(--secondary)] transition-colors hover:font-medium hover:text-[var(--primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]"
                                >
                                  {sub.name}
                                </a>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="py-4 text-xs text-[var(--secondary)]">
                  Keine Unterkategorien vorhanden.
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Fallback Panel wenn keine ASL-Kategorien geladen (loading/unavailable) */}
      {aslMenuRoots.length === 0 && (
        <div
          id="enterprise-mega-menu-panel"
          data-testid="enterprise-mega-menu-panel"
          aria-label="Produktkategorien"
          className="hidden lg:block invisible absolute left-0 right-0 top-full z-50 w-full border-b border-[var(--border)] bg-white shadow-2xl -translate-y-1 opacity-0 pointer-events-none"
        >
          <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between border-b border-[var(--border)] pb-3 mb-4">
              <div className="flex items-center gap-2">
                <Layers className="h-4 w-4 text-[var(--primary)]" />
                <h4 className="font-display text-sm font-bold uppercase tracking-wider text-[var(--foreground)]">
                  System-Fachbereiche &amp; Produktfamilien
                </h4>
              </div>
              <button
                onClick={() => handleNav('/catalog')}
                className="text-xs font-bold text-[var(--primary)] hover:underline cursor-pointer"
              >
                Gesamten Katalog ansehen &rarr;
              </button>
            </div>
            <div className="grid grid-cols-5 gap-6" />
          </div>
        </div>
      )}

      {/* Search Overlay */}
      <SearchOverlay
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        tenant={tenant || ctxTenant}
        onNavigate={handleNav}
        onSearch={onSearch}
      />
    </header>
  );
}